import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useVideoStream } from "../../providers/videoStreams";
import {
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  FormControlLabel,
  Switch,
  CircularProgress,
  Alert,
} from "@mui/material";
import { FiEdit } from "react-icons/fi";
import PageHeader from "../../components/pageHeader";
import { Container } from "./styles";

const EditVideoStream = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentVideoStream, getVideoStreamById, updateVideoStream } =
    useVideoStream();
  const { path } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const loadVideo = async () => {
      if (id) {
        setIsLoading(true);
        const video = await getVideoStreamById(id);

        if (video) {
          setName(video.name);
          setDescription(video.description || "");
          setIsActive(video.isActive);
        }

        setIsLoading(false);
      }
    };

    loadVideo();
  }, [id]);

  const validateForm = () => {
    if (!name.trim()) {
      setError(t("video_name_required"));
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateForm() || !id) return;

    setIsSaving(true);
    setError("");

    try {
      await updateVideoStream(id, {
        name,
        description,
        isActive,
      });

      // Navigate back to details page
      navigate(`/video-streams/${id}`);
    } catch (err: any) {
      setError(err.message || t("update_error"));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Container>
      <PageHeader
        title={t("edit_video")}
        description={t("edit_video_description")}
        buttonContent={t("back_to_video")}
        buttonPath={`/video-streams/${id}`}
        icon={<FiEdit />}
      />

      <Paper className="editContainer">
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Box p={3}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {currentVideoStream ? (
              <>
                <TextField
                  label={t("video_name")}
                  fullWidth
                  margin="normal"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />

                <TextField
                  label={t("video_description")}
                  fullWidth
                  margin="normal"
                  multiline
                  rows={4}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                    />
                  }
                  label={t("video_active")}
                />

                <Box mt={4} display="flex" justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    onClick={() => navigate(`/${path}/video-streams`)}
                    sx={{ mr: 2 }}
                    disabled={isSaving}
                  >
                    {t("cancel")}
                  </Button>

                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={isSaving}
                    startIcon={isSaving ? <CircularProgress size={20} /> : null}
                  >
                    {isSaving ? t("saving") : t("save")}
                  </Button>
                </Box>
              </>
            ) : (
              <Typography color="error">{t("video_not_found")}</Typography>
            )}
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default EditVideoStream;
